import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import { toast } from 'react-toastify';

const QueueManagement = () => {
  const [events, setEvents]         = useState([]);
  const [selected, setSelected]     = useState(null);  // eventId
  const [queueData, setQueueData]   = useState(null);
  const [loading, setLoading]       = useState(false);
  const [saving, setSaving]         = useState(false);
  const [form, setForm]             = useState({
    queue_enabled:             false,
    max_concurrent_sessions:   50,
    purchase_session_duration: 600,
    queue_status:              'active',
  });

  useEffect(() => {
    api.get('/admin/events').then(r => {
      setEvents(r.data?.data?.events?.data || r.data?.data || []);
    }).catch(() => {});
  }, []);

  const loadQueue = useCallback(async (eventId) => {
    setLoading(true);
    setQueueData(null);
    try {
      const res = await api.get(`/admin/events/${eventId}/queue`);
      const d   = res.data.data;
      setQueueData(d);
      setForm({
        queue_enabled:             d.config.queue_enabled,
        max_concurrent_sessions:   d.config.max_concurrent_sessions,
        purchase_session_duration: d.config.purchase_session_duration,
        queue_status:              d.config.queue_status,
      });
    } catch {
      toast.error('Failed to load queue data.');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSelectEvent = (e) => {
    const id = parseInt(e.target.value);
    setSelected(id || null);
    if (id) loadQueue(id);
  };

  const handleSave = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await api.put(`/admin/events/${selected}/queue`, form);
      toast.success('Queue settings saved.');
      loadQueue(selected);
    } catch {
      toast.error('Failed to save queue settings.');
    } finally {
      setSaving(false);
    }
  };

  const handleAction = async (action) => {
    if (!selected) return;
    try {
      if (action === 'clear') {
        await api.delete(`/admin/events/${selected}/queue/expired`);
        toast.success('Expired entries cleared.');
      } else {
        await api.post(`/admin/events/${selected}/queue/${action}`);
        toast.success(`Queue ${action}d.`);
      }
      loadQueue(selected);
    } catch {
      toast.error(`Failed to ${action} queue.`);
    }
  };

  const statusColor = {
    active: 'badge-success',
    paused: 'badge-warning',
    closed: 'badge-danger',
  };

  return (
    <div>
      <div className="d-sm-flex align-items-center justify-content-between mb-4">
        <h5 className="h5 mb-0 font-weight-bold text-gray-900">Queue Management</h5>
      </div>

      {/* Event picker */}
      <div className="card shadow mb-4">
        <div className="card-body">
          <label className="block text-sm font-medium text-gray-700 mb-2">Select Event</label>
          <select
            className="form-control"
            value={selected || ''}
            onChange={handleSelectEvent}
            style={{ maxWidth: 400 }}
          >
            <option value="">— Choose an event —</option>
            {events.map(ev => (
              <option key={ev.id} value={ev.id}>{ev.title}</option>
            ))}
          </select>
        </div>
      </div>

      {loading && (
        <div className="text-center py-8">
          <div className="spinner-border text-warning" role="status" />
        </div>
      )}

      {queueData && !loading && (
        <>
          {/* Config form */}
          <div className="card shadow mb-4">
            <div className="card-header py-3">
              <h6 className="m-0 font-weight-bold text-gray-900">Queue Settings</h6>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-3 mb-3">
                  <label className="text-sm font-medium text-gray-700">Queue Enabled</label>
                  <div className="mt-2">
                    <div className="custom-control custom-switch">
                      <input
                        type="checkbox"
                        className="custom-control-input"
                        id="queueEnabled"
                        checked={form.queue_enabled}
                        onChange={e => setForm(f => ({ ...f, queue_enabled: e.target.checked }))}
                      />
                      <label className="custom-control-label" htmlFor="queueEnabled">
                        {form.queue_enabled ? 'On' : 'Off'}
                      </label>
                    </div>
                  </div>
                </div>
                <div className="col-md-3 mb-3">
                  <label className="text-sm font-medium text-gray-700">Max Concurrent Sessions</label>
                  <input
                    type="number"
                    className="form-control mt-1"
                    min="1"
                    value={form.max_concurrent_sessions}
                    onChange={e => setForm(f => ({ ...f, max_concurrent_sessions: parseInt(e.target.value) || 1 }))}
                  />
                </div>
                <div className="col-md-3 mb-3">
                  <label className="text-sm font-medium text-gray-700">Purchase Session (seconds)</label>
                  <input
                    type="number"
                    className="form-control mt-1"
                    min="60"
                    step="60"
                    value={form.purchase_session_duration}
                    onChange={e => setForm(f => ({ ...f, purchase_session_duration: parseInt(e.target.value) || 60 }))}
                  />
                  <small className="text-muted">{Math.round(form.purchase_session_duration / 60)} min</small>
                </div>
                <div className="col-md-3 mb-3">
                  <label className="text-sm font-medium text-gray-700">Queue Status</label>
                  <select
                    className="form-control mt-1"
                    value={form.queue_status}
                    onChange={e => setForm(f => ({ ...f, queue_status: e.target.value }))}
                  >
                    <option value="active">Active</option>
                    <option value="paused">Paused</option>
                    <option value="closed">Closed</option>
                  </select>
                </div>
              </div>
              <button
                className="btn btn-warning btn-sm"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saving…' : 'Save Settings'}
              </button>
            </div>
          </div>

          {/* Stats */}
          <div className="row mb-4">
            {[
              { label: 'Waiting',           value: queueData.stats.total_waiting,   color: 'warning' },
              { label: 'Active Sessions',   value: queueData.stats.total_active,    color: 'success' },
              { label: 'Available Slots',   value: queueData.stats.available_slots, color: 'info' },
              { label: 'Queue Status',      value: queueData.config.queue_status,   color: 'secondary', badge: true },
            ].map(({ label, value, color, badge }) => (
              <div key={label} className="col-xl-3 col-md-6 mb-4">
                <div className={`card border-left-${color} shadow h-100 py-2`}>
                  <div className="card-body">
                    <div className="text-xs font-weight-bold text-uppercase mb-1 text-gray-600">{label}</div>
                    {badge ? (
                      <span className={`badge ${statusColor[value] || 'badge-secondary'} p-2`}>
                        {value}
                      </span>
                    ) : (
                      <div className="h5 mb-0 font-weight-bold text-gray-800">{value}</div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Admin actions */}
          <div className="card shadow mb-4">
            <div className="card-header py-3">
              <h6 className="m-0 font-weight-bold text-gray-900">Quick Actions</h6>
            </div>
            <div className="card-body d-flex gap-3 flex-wrap">
              <button className="btn btn-success btn-sm" onClick={() => handleAction('resume')}>
                <i className="fas fa-play mr-1" /> Resume Queue
              </button>
              <button className="btn btn-warning btn-sm" onClick={() => handleAction('pause')}>
                <i className="fas fa-pause mr-1" /> Pause Queue
              </button>
              <button className="btn btn-danger btn-sm" onClick={() => handleAction('close')}>
                <i className="fas fa-stop mr-1" /> Close Queue
              </button>
              <button className="btn btn-secondary btn-sm" onClick={() => handleAction('clear')}>
                <i className="fas fa-trash mr-1" /> Clear Expired
              </button>
              <button className="btn btn-outline-primary btn-sm" onClick={() => loadQueue(selected)}>
                <i className="fas fa-sync mr-1" /> Refresh
              </button>
            </div>
          </div>

          {/* Waiting list */}
          {queueData.waiting?.length > 0 && (
            <div className="card shadow mb-4">
              <div className="card-header py-3">
                <h6 className="m-0 font-weight-bold text-gray-900">
                  Waiting Users ({queueData.waiting.length})
                </h6>
              </div>
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-sm mb-0">
                    <thead className="thead-light">
                      <tr>
                        <th>#</th>
                        <th>Entry ID</th>
                        <th>User ID</th>
                        <th>Joined At</th>
                        <th>Last Heartbeat</th>
                      </tr>
                    </thead>
                    <tbody>
                      {queueData.waiting.map((w, i) => (
                        <tr key={w.entry_id} style={{ fontSize: '0.85rem' }}>
                          <td>{i + 1}</td>
                          <td>#{w.entry_id}</td>
                          <td>{w.user_id}</td>
                          <td>{w.joined_at ? new Date(w.joined_at).toLocaleTimeString() : '—'}</td>
                          <td>{w.last_heartbeat_at ? new Date(w.last_heartbeat_at).toLocaleTimeString() : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Active sessions */}
          {queueData.active?.length > 0 && (
            <div className="card shadow mb-4">
              <div className="card-header py-3">
                <h6 className="m-0 font-weight-bold text-gray-900">
                  Active Sessions ({queueData.active.length})
                </h6>
              </div>
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-sm mb-0">
                    <thead className="thead-light">
                      <tr>
                        <th>Entry ID</th>
                        <th>User ID</th>
                        <th>Status</th>
                        <th>Admitted At</th>
                        <th>Expires At</th>
                      </tr>
                    </thead>
                    <tbody>
                      {queueData.active.map(a => (
                        <tr key={a.entry_id} style={{ fontSize: '0.85rem' }}>
                          <td>#{a.entry_id}</td>
                          <td>{a.user_id}</td>
                          <td>
                            <span className={`badge ${a.status === 'purchasing' ? 'badge-success' : 'badge-info'}`}>
                              {a.status}
                            </span>
                          </td>
                          <td>{a.admitted_at ? new Date(a.admitted_at).toLocaleTimeString() : '—'}</td>
                          <td>{a.expires_at ? new Date(a.expires_at).toLocaleTimeString() : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default QueueManagement;
